<template>
  <div class="analytics-reports-page space-y-5 stagger-children">
    <!-- Page Header -->
    <div>
      <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
        Analytics Reports
      </h1>
      <p class="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
        Generate, email, and schedule analytics reports
      </p>
    </div>

    <!-- Success / Error Banners -->
    <AlertBanner v-if="successMsg" type="success" :message="successMsg" @dismiss="successMsg = ''" />
    <AlertBanner v-if="error" type="error" :message="error" @dismiss="error = null" />

    <!-- Section 1: Report Builder Card -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-6 space-y-5">
      <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Report Builder</h2>

      <!-- Scope Selector -->
      <div>
        <label id="rb-scope-label" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">Scope</label>
        <div class="flex gap-2 flex-wrap" role="group" aria-labelledby="rb-scope-label">
          <button
            v-for="opt in scopeOptions"
            :key="opt.value"
            @click="scope = opt.value"
            :aria-pressed="(scope === opt.value).toString()"
            :class="[
              'px-4 py-2 rounded-lg text-sm font-medium transition-all',
              scope === opt.value
                ? 'bg-primary-700 text-white shadow-sm'
                : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600'
            ]"
          >{{ opt.label }}</button>
        </div>

        <!-- Supplier Group Dropdown -->
        <div v-if="scope === 'group'" class="mt-3">
          <label for="rb-group-select" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5">Select Group</label>
          <select
            id="rb-group-select"
            v-model="selectedGroupId"
            class="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200 text-sm"
          >
            <option value="">-- Choose a group --</option>
            <option v-for="g in supplierGroups" :key="g.id" :value="g.id">{{ g.name }}</option>
          </select>
        </div>

        <!-- Individual Suppliers Multi-select -->
        <div v-if="scope === 'individual'" class="mt-3">
          <label for="rb-suppliers" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5">Select Suppliers</label>
          <v-select
            v-model="selectedSupplierIds"
            input-id="rb-suppliers"
            :options="supplierOptions"
            :multiple="true"
            label="label"
            :reduce="opt => opt.value"
            placeholder="Search suppliers..."
            class="v-select-custom"
          />
        </div>

        <!-- Supplier Locations picker -->
        <div v-if="scope === 'locations'" class="mt-3 space-y-3">
          <div>
            <label for="rb-loc-supplier" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5">Select Supplier</label>
            <v-select
              v-model="selectedLocationSupplierId"
              input-id="rb-loc-supplier"
              :options="locationSupplierOptions"
              label="label"
              :reduce="opt => opt.value"
              placeholder="Search suppliers with locations..."
              class="v-select-custom"
            />
          </div>
          <div v-if="selectedLocationSupplierId && selectedSupplierLocations.length">
            <div class="flex items-center justify-between mb-1.5">
              <label class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300">Locations</label>
              <div class="flex gap-2">
                <button type="button" @click="selectedLocationIds = selectedSupplierLocations.map(l => l.locationid)" class="text-xs font-medium text-primary-700 dark:text-primary-400 hover:underline">Select All</button>
                <button type="button" @click="selectedLocationIds = []" class="text-xs font-medium text-neutral-500 dark:text-neutral-400 hover:underline">Clear</button>
              </div>
            </div>
            <div class="space-y-1.5 max-h-56 overflow-auto rounded-lg border border-neutral-200 dark:border-neutral-700 p-3">
              <label v-for="loc in selectedSupplierLocations" :key="loc.locationid" class="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                <input type="checkbox" :value="loc.locationid" v-model="selectedLocationIds" class="rounded border-neutral-300 text-primary-600 focus:ring-primary-500" />
                <span>{{ loc.locationname }}</span>
              </label>
            </div>
            <p v-if="selectedLocationIds.length" class="text-xs text-neutral-400 dark:text-neutral-500 mt-1">{{ selectedLocationIds.length }} location(s) selected</p>
          </div>
          <p v-else-if="selectedLocationSupplierId" class="text-xs text-amber-600 dark:text-amber-400">This supplier has no locations — use “Individual Suppliers” instead.</p>
        </div>
      </div>

      <!-- Date Range -->
      <div>
        <label class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">Date Range</label>
        <div class="flex flex-wrap items-center gap-2.5">
          <input
            v-model="startDate"
            type="date"
            :max="maxDate"
            aria-label="Start date"
            class="px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg text-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
          />
          <span class="text-neutral-400 dark:text-neutral-500 text-sm">to</span>
          <input
            v-model="endDate"
            type="date"
            :max="maxDate"
            aria-label="End date"
            class="px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg text-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
          />
        </div>
      </div>

      <!-- Format Toggle -->
      <div>
        <label id="rb-format-label" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">Format</label>
        <div class="flex gap-2 flex-wrap" role="group" aria-labelledby="rb-format-label">
          <button
            v-for="opt in formatOptions"
            :key="opt.value"
            @click="format = opt.value"
            :aria-pressed="(format === opt.value).toString()"
            :class="[
              'px-4 py-2 rounded-lg text-sm font-medium transition-all',
              format === opt.value
                ? 'bg-primary-700 text-white shadow-sm'
                : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600'
            ]"
          >{{ opt.label }}</button>
        </div>
      </div>

      <!-- Email Recipients -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <label class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300">Email Recipients</label>
          <button
            type="button"
            @click="recipientList.push('')"
            class="text-xs font-medium text-primary-700 dark:text-primary-400 hover:underline"
          >+ Add Recipient</button>
        </div>
        <div class="space-y-2">
          <div v-for="(email, idx) in recipientList" :key="idx">
            <div class="flex gap-2">
              <input
                v-model="recipientList[idx]"
                type="email"
                placeholder="email@example.com"
                :aria-label="`Recipient email ${idx + 1}`"
                :aria-invalid="recipientErrors[idx] ? 'true' : null"
                :aria-describedby="recipientErrors[idx] ? `rb-recipient-error-${idx}` : null"
                @blur="validateRecipient(idx)"
                @input="recipientErrors[idx] = ''"
                :class="[
                  'flex-1 px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200 text-sm',
                  recipientErrors[idx] ? 'border-red-500 dark:border-red-500' : 'border-neutral-300 dark:border-neutral-600'
                ]"
              />
              <button
                type="button"
                @click="removeRecipient(idx)"
                v-if="recipientList.length > 1"
                aria-label="Remove recipient"
                class="p-2 rounded-lg text-neutral-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-all"
              >
                <span class="material-icons text-[18px]" aria-hidden="true">close</span>
              </button>
            </div>
            <p v-if="recipientErrors[idx]" :id="`rb-recipient-error-${idx}`" class="text-xs text-red-600 dark:text-red-400 mt-1">
              {{ recipientErrors[idx] }}
            </p>
          </div>
        </div>
        <p class="text-xs text-neutral-500 dark:text-neutral-400 mt-1.5">Leave empty to download only.</p>
      </div>

      <!-- Schedule Toggle -->
      <div>
        <label class="inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            v-model="scheduleEnabled"
            type="checkbox"
            class="rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500 dark:bg-neutral-700"
          />
          <span class="text-sm font-semibold text-neutral-700 dark:text-neutral-300">Schedule this report</span>
        </label>

        <div v-if="scheduleEnabled" class="mt-3 space-y-3 pl-6 border-l-2 border-primary-200 dark:border-primary-800">
          <!-- Frequency Buttons -->
          <div>
            <label id="rb-frequency-label" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5">Frequency</label>
            <div class="flex gap-2 flex-wrap" role="group" aria-labelledby="rb-frequency-label">
              <button
                v-for="opt in frequencyOptions"
                :key="opt.value"
                @click="frequency = opt.value"
                :aria-pressed="(frequency === opt.value).toString()"
                :class="[
                  'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                  frequency === opt.value
                    ? 'bg-primary-700 text-white shadow-sm'
                    : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600'
                ]"
              >{{ opt.label }}</button>
            </div>
          </div>

          <!-- Schedule Name -->
          <div>
            <label for="rb-schedule-name" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5">
              Schedule Name <span class="text-red-500" aria-hidden="true">*</span>
            </label>
            <input
              id="rb-schedule-name"
              ref="scheduleNameInput"
              v-model="scheduleName"
              type="text"
              placeholder="e.g. Monthly All-Supplier Report"
              :aria-invalid="scheduleNameError ? 'true' : null"
              :aria-describedby="scheduleNameError ? 'rb-schedule-name-error' : null"
              @input="scheduleNameError = ''"
              :class="[
                'w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200 text-sm',
                scheduleNameError ? 'border-red-500 dark:border-red-500' : 'border-neutral-300 dark:border-neutral-600'
              ]"
            />
            <p v-if="scheduleNameError" id="rb-schedule-name-error" class="text-xs text-red-600 dark:text-red-400 mt-1">
              {{ scheduleNameError }}
            </p>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex flex-wrap items-center gap-2.5 pt-2 border-t border-neutral-100 dark:border-neutral-700">
        <button
          @click="handleDownload"
          :disabled="!!pendingAction || !canGenerate"
          class="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary-700 text-white hover:bg-primary-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg v-if="pendingAction === 'download' || pendingAction === 'schedule'" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span v-else class="material-icons text-[18px]" aria-hidden="true">{{ scheduleEnabled ? 'event_repeat' : 'download' }}</span>
          {{ scheduleEnabled ? 'Create Schedule' : 'Download Report' }}
        </button>
        <button
          @click="handleEmail"
          :disabled="!!pendingAction || !hasRecipients || !canGenerate"
          class="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-semibold bg-secondary-600 text-white hover:bg-secondary-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg v-if="pendingAction === 'email'" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span v-else class="material-icons text-[18px]" aria-hidden="true">email</span>
          Email Report
        </button>
        <button
          @click="handleEmailAndDownload"
          :disabled="!!pendingAction || !hasRecipients || !canGenerate"
          class="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-semibold border border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg v-if="pendingAction === 'emailDownload'" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span v-else class="material-icons text-[18px]" aria-hidden="true">send</span>
          Email + Download
        </button>
      </div>
    </div>

    <!-- Section 2: Tabs (History / Schedules) -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
      <!-- Tab Buttons -->
      <div class="flex border-b border-neutral-200 dark:border-neutral-700" role="tablist" aria-label="Reports">
        <button
          id="reports-tab-history"
          role="tab"
          :aria-selected="(activeTab === 'history').toString()"
          aria-controls="reports-panel-history"
          @click="setTab('history')"
          :class="[
            'flex-1 px-6 py-3 text-sm font-semibold transition-all duration-200 border-b-2',
            activeTab === 'history'
              ? 'border-primary-600 text-primary-600 dark:text-primary-400 bg-primary-50/50 dark:bg-primary-900/10'
              : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700/50'
          ]"
        >
          <span class="inline-flex items-center gap-1.5">
            <span class="material-icons text-[18px]" aria-hidden="true">history</span>
            Report History
          </span>
        </button>
        <button
          id="reports-tab-schedules"
          role="tab"
          :aria-selected="(activeTab === 'schedules').toString()"
          aria-controls="reports-panel-schedules"
          @click="setTab('schedules')"
          :class="[
            'flex-1 px-6 py-3 text-sm font-semibold transition-all duration-200 border-b-2',
            activeTab === 'schedules'
              ? 'border-primary-600 text-primary-600 dark:text-primary-400 bg-primary-50/50 dark:bg-primary-900/10'
              : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700/50'
          ]"
        >
          <span class="inline-flex items-center gap-1.5">
            <span class="material-icons text-[18px]" aria-hidden="true">schedule</span>
            Scheduled Reports
          </span>
        </button>
      </div>

      <!-- History Tab (v-show keeps DataTable search/sort/page state alive across switches) -->
      <div v-show="activeTab === 'history'" id="reports-panel-history" role="tabpanel" aria-labelledby="reports-tab-history" class="p-6">
        <DataTable
          :data="reportHistory"
          :columns="historyColumns"
          :loading="historyLoading"
          :searchable="true"
          :pagination="true"
          loading-text="Loading report history..."
          empty-text="No reports generated yet"
        >
          <template #cell-generatedAt="{ value }">
            <span class="text-sm text-neutral-700 dark:text-neutral-300">{{ formatDateTime(value) }}</span>
          </template>

          <template #cell-scope="{ value }">
            <span
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-100 dark:border-primary-800/30"
            >{{ scopeLabel(value) }}</span>
          </template>

          <template #cell-period="{ row }">
            <span class="text-sm text-neutral-700 dark:text-neutral-300">
              {{ row.startDate }} to {{ row.endDate }}
            </span>
          </template>

          <template #cell-format="{ value }">
            <span class="text-sm font-medium text-neutral-700 dark:text-neutral-300 uppercase">{{ value }}</span>
          </template>

          <template #cell-emailedTo="{ value }">
            <span class="text-sm text-neutral-600 dark:text-neutral-400">
              {{ value && value.length ? value.join(', ') : '--' }}
            </span>
          </template>

          <template #actions="{ row }">
            <div class="flex items-center gap-1 justify-center">
              <button
                @click="resendReport(row)"
                :disabled="!row.emailedTo || !row.emailedTo.length || pendingResendRow === row"
                aria-label="Re-send report"
                class="p-1.5 rounded-lg text-neutral-400 hover:text-secondary-600 hover:bg-secondary-50 dark:hover:text-secondary-400 dark:hover:bg-secondary-900/30 transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Re-send report"
              >
                <svg v-if="pendingResendRow === row" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span v-else class="material-icons text-[16px]" aria-hidden="true">forward_to_inbox</span>
              </button>
            </div>
          </template>
        </DataTable>
      </div>

      <!-- Schedules Tab -->
      <div v-show="activeTab === 'schedules'" id="reports-panel-schedules" role="tabpanel" aria-labelledby="reports-tab-schedules" class="p-6">
        <DataTable
          :data="schedules"
          :columns="scheduleColumns"
          :loading="schedulesLoading"
          :searchable="true"
          :pagination="true"
          loading-text="Loading scheduled reports..."
          empty-text="No scheduled reports"
        >
          <template #cell-name="{ value }">
            <span class="text-sm font-medium text-neutral-900 dark:text-neutral-100">{{ value }}</span>
          </template>

          <template #cell-scope="{ value }">
            <span
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-100 dark:border-primary-800/30"
            >{{ scopeLabel(value) }}</span>
          </template>

          <template #cell-frequency="{ value }">
            <span class="text-sm font-medium text-neutral-700 dark:text-neutral-300 capitalize">{{ value }}</span>
          </template>

          <template #cell-lastRun="{ value }">
            <span class="text-sm text-neutral-700 dark:text-neutral-300">{{ value ? formatDateTime(value) : 'Never' }}</span>
          </template>

          <template #cell-status="{ row }">
            <div class="flex flex-col gap-0.5">
              <span v-if="!row.status" class="inline-flex w-fit items-center px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400">
                Pending
              </span>
              <span v-else-if="row.status === 'success'" class="inline-flex w-fit items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400">
                <span class="material-icons text-[12px]" aria-hidden="true">check_circle</span>
                Success
              </span>
              <template v-else-if="row.status === 'skipped'">
                <span class="inline-flex w-fit items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400">
                  <span class="material-icons text-[12px]" aria-hidden="true">remove_circle_outline</span>
                  Skipped
                </span>
                <span v-if="row.lastRunError" class="text-[11px] leading-tight text-amber-700 dark:text-amber-400/80 max-w-[300px] line-clamp-2" :title="row.lastRunError">{{ row.lastRunError }}</span>
              </template>
              <template v-else>
                <span class="inline-flex w-fit items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400">
                  <span class="material-icons text-[12px]" aria-hidden="true">error</span>
                  Failed
                </span>
                <span class="text-[11px] leading-tight text-red-600 dark:text-red-400/90 max-w-[300px] line-clamp-2" :title="row.lastRunError || 'Unknown error'">{{ row.lastRunError || 'Unknown error' }}</span>
              </template>
            </div>
          </template>

          <template #cell-nextRun="{ value }">
            <span class="text-sm text-neutral-700 dark:text-neutral-300">{{ value ? formatDateTime(value) : '--' }}</span>
          </template>

          <template #cell-runCount="{ value }">
            <span class="text-sm font-semibold tabular-nums text-neutral-700 dark:text-neutral-300">{{ value || 0 }}</span>
          </template>

          <template #cell-enabled="{ row }">
            <button
              @click="toggleScheduleEnabled(row)"
              role="switch"
              :aria-checked="row.enabled ? 'true' : 'false'"
              aria-label="Toggle schedule enabled"
              :disabled="pendingToggleId === row.id"
              :class="[
                'relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800',
                row.enabled ? 'bg-primary-600' : 'bg-neutral-300 dark:bg-neutral-600',
                pendingToggleId === row.id ? 'opacity-50 cursor-wait' : ''
              ]"
            >
              <span
                :class="[
                  'inline-block h-3.5 w-3.5 rounded-full bg-white shadow transform transition-transform duration-200',
                  row.enabled ? 'translate-x-[18px]' : 'translate-x-[3px]'
                ]"
              ></span>
            </button>
          </template>

          <template #actions="{ row }">
            <div class="flex items-center gap-1 justify-center">
              <button
                @click="openEditSchedule(row)"
                aria-label="Edit schedule"
                class="p-1.5 rounded-lg text-neutral-400 hover:text-secondary-600 hover:bg-secondary-50 dark:hover:text-secondary-400 dark:hover:bg-secondary-900/30 transition-all duration-150"
                title="Edit schedule"
              >
                <span class="material-icons text-[16px]" aria-hidden="true">edit</span>
              </button>
              <button
                @click="handleRunNow(row)"
                aria-label="Run schedule now"
                :disabled="!!pendingRunId"
                class="p-1.5 rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/30 transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Run now"
              >
                <svg v-if="pendingRunId === row.id" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span v-else class="material-icons text-[16px]" aria-hidden="true">play_arrow</span>
              </button>
              <button
                @click="confirmDeleteSchedule(row)"
                aria-label="Delete schedule"
                class="p-1.5 rounded-lg text-neutral-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/30 transition-all duration-150"
                title="Delete schedule"
              >
                <span class="material-icons text-[16px]" aria-hidden="true">delete_outline</span>
              </button>
            </div>
          </template>
        </DataTable>
      </div>
    </div>

    <!-- Edit Schedule Modal -->
    <Modal :show="showEditModal" title="Edit Scheduled Report" size="lg" @close="showEditModal = false">
      <form @submit.prevent="saveScheduleEdit" class="space-y-4">
        <div>
          <label for="em-name" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5">Report Name</label>
          <input
            id="em-name"
            v-model="editForm.name"
            type="text"
            class="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-neutral-50 dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Report name"
          />
        </div>

        <div>
          <label id="em-frequency-label" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5">Frequency</label>
          <div class="flex gap-2" role="group" aria-labelledby="em-frequency-label">
            <button
              v-for="freq in ['monthly', 'weekly', 'biweekly']"
              :key="freq"
              type="button"
              @click="editForm.frequency = freq"
              :aria-pressed="(editForm.frequency === freq).toString()"
              :class="[
                'px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize',
                editForm.frequency === freq
                  ? 'bg-primary-700 text-white shadow-sm'
                  : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600'
              ]"
            >{{ freq === 'biweekly' ? 'Bi-weekly' : freq }}</button>
          </div>
        </div>

        <div>
          <label id="em-format-label" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5">Format</label>
          <div class="flex gap-2" role="group" aria-labelledby="em-format-label">
            <button
              v-for="fmt in ['pdf', 'csv', 'both']"
              :key="fmt"
              type="button"
              @click="editForm.format = fmt"
              :aria-pressed="(editForm.format === fmt).toString()"
              :class="[
                'px-4 py-2 rounded-lg text-sm font-medium transition-all uppercase',
                editForm.format === fmt
                  ? 'bg-primary-700 text-white shadow-sm'
                  : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600'
              ]"
            >{{ fmt === 'csv' ? 'CSV / Excel' : fmt }}</button>
          </div>
        </div>

        <div v-if="editForm.scope !== 'locations'">
          <div class="flex items-center justify-between mb-1.5">
            <label for="em-suppliers" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300">Suppliers</label>
            <div class="flex gap-2">
              <button
                type="button"
                @click="editForm.supplierIds = supplierOptions.map(o => o.value)"
                class="text-xs font-medium text-primary-700 dark:text-primary-400 hover:underline"
              >Select All</button>
              <button
                type="button"
                @click="editForm.supplierIds = []"
                class="text-xs font-medium text-neutral-500 dark:text-neutral-400 hover:underline"
              >Clear</button>
            </div>
          </div>
          <v-select
            v-model="editForm.supplierIds"
            input-id="em-suppliers"
            :options="supplierOptions"
            :multiple="true"
            :close-on-select="false"
            label="label"
            :reduce="opt => opt.value"
            placeholder="Search and select suppliers..."
            class="v-select-custom"
          />
          <p class="text-xs text-neutral-400 dark:text-neutral-500 mt-1" v-if="editForm.supplierIds.length">
            {{ editForm.supplierIds.length }} supplier{{ editForm.supplierIds.length !== 1 ? 's' : '' }} selected
          </p>
        </div>

        <div>
          <div class="flex items-center justify-between mb-1.5">
            <label class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300">Email Recipients</label>
            <button
              type="button"
              @click="addRecipientField"
              class="text-xs font-medium text-primary-700 dark:text-primary-400 hover:underline"
            >+ Add Recipient</button>
          </div>
          <div class="space-y-2">
            <div v-for="(email, idx) in editForm.recipients" :key="idx" class="flex gap-2">
              <input
                v-model="editForm.recipients[idx]"
                type="email"
                :aria-label="`Recipient email ${idx + 1}`"
                class="flex-1 px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-neutral-50 dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="email@example.com"
              />
              <button
                type="button"
                @click="editForm.recipients.splice(idx, 1)"
                v-if="editForm.recipients.length > 1"
                aria-label="Remove recipient"
                class="p-2 rounded-lg text-neutral-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-all"
              >
                <span class="material-icons text-[18px]" aria-hidden="true">close</span>
              </button>
            </div>
          </div>
          <p class="text-xs text-neutral-400 dark:text-neutral-500 mt-1.5">{{ editForm.recipients.filter(e => e.trim()).length }} recipient(s)</p>
        </div>

        <div class="flex items-center gap-3">
          <label class="text-sm font-semibold text-neutral-700 dark:text-neutral-300">Enabled</label>
          <button
            type="button"
            @click="editForm.enabled = !editForm.enabled"
            role="switch"
            :aria-checked="editForm.enabled ? 'true' : 'false'"
            aria-label="Toggle schedule enabled"
            :class="[
              'relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200',
              editForm.enabled ? 'bg-primary-600' : 'bg-neutral-300 dark:bg-neutral-600'
            ]"
          >
            <span
              :class="[
                'inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform duration-200',
                editForm.enabled ? 'translate-x-[22px]' : 'translate-x-[3px]'
              ]"
            ></span>
          </button>
        </div>

        <!-- Schedule Info (read-only) -->
        <div class="bg-neutral-50 dark:bg-neutral-900 rounded-lg p-4 space-y-2">
          <div class="flex justify-between text-sm">
            <span class="text-neutral-500 dark:text-neutral-400">Scope</span>
            <span class="text-neutral-700 dark:text-neutral-300 font-medium">{{ scopeLabel(editForm.scope) }}</span>
          </div>
          <div v-if="editForm.scope === 'locations'" class="flex justify-between text-sm">
            <span class="text-neutral-500 dark:text-neutral-400">Locations</span>
            <span class="text-neutral-700 dark:text-neutral-300 text-right max-w-[70%]">{{ editLocationLabel }}</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-neutral-500 dark:text-neutral-400">Last Run</span>
            <span class="text-neutral-700 dark:text-neutral-300">{{ editForm.lastRunAt ? formatDateTime(editForm.lastRunAt) : 'Never' }}</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-neutral-500 dark:text-neutral-400">Next Run</span>
            <span class="text-neutral-700 dark:text-neutral-300">{{ editForm.nextRunAt ? formatDateTime(editForm.nextRunAt) : '--' }}</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-neutral-500 dark:text-neutral-400">Total Runs</span>
            <span class="text-neutral-700 dark:text-neutral-300 font-medium">{{ editForm.runCount || 0 }}</span>
          </div>
          <div v-if="editForm.lastRunStatus === 'failed' || editForm.lastRunStatus === 'skipped'" class="text-sm">
            <div class="mb-1" :class="editForm.lastRunStatus === 'failed' ? 'text-red-500' : 'text-amber-600 dark:text-amber-400'">
              {{ editForm.lastRunStatus === 'failed' ? 'Failure reason' : 'Skip reason' }}
            </div>
            <div
              class="text-xs rounded-lg px-2.5 py-2 whitespace-pre-wrap break-words"
              :class="editForm.lastRunStatus === 'failed'
                ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'"
            >{{ editForm.lastRunError || 'No details recorded.' }}</div>
          </div>
        </div>
      </form>

      <template #footer>
        <div class="flex items-center justify-end gap-2.5">
          <button
            @click="showEditModal = false"
            class="px-4 py-2.5 rounded-lg text-sm font-medium text-neutral-600 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-all"
          >Cancel</button>
          <button
            @click="saveScheduleEdit"
            :disabled="editSaving"
            class="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary-700 text-white hover:bg-primary-800 transition-all disabled:opacity-50"
          >
            <span v-if="editSaving" class="inline-flex items-center gap-1.5">
              <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
              Saving...
            </span>
            <span v-else>Save Changes</span>
          </button>
        </div>
      </template>
    </Modal>

    <!-- Delete Schedule Confirmation -->
    <ConfirmDialog
      :show="deleteState.show"
      title="Delete schedule"
      :message="deleteState.row ? `Delete schedule “${deleteState.row.name}”? This cannot be undone.` : ''"
      confirm-label="Delete"
      :danger="true"
      :loading="deleteState.loading"
      @confirm="doDeleteSchedule"
      @cancel="deleteState.show = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, watchEffect, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore'
import { db } from '@/firebase/config'
import { useAnalytics } from '@/composables/useAnalytics'
import { useSupplierGroups } from '@/composables/useSupplierGroups'
import DataTable from '@/components/ui/DataTable.vue'
import Modal from '@/components/ui/Modal.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import AlertBanner from '@/components/ui/AlertBanner.vue'
import { formatDateTime } from '@/utils/format'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
  name: 'AnalyticsReports',
  components: {
    DataTable,
    Modal,
    ConfirmDialog,
    AlertBanner,
    vSelect,
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const {
      error,
      downloadReport,
      emailReport,
      fetchReportHistory,
      fetchSchedules: fetchSchedulesApi,
      createSchedule,
      updateSchedule,
      deleteSchedule: deleteScheduleApi,
      runScheduleNow,
    } = useAnalytics()

    const { groups: supplierGroups, fetchGroups } = useSupplierGroups()

    // ---- Report Builder State ----
    const scopeOptions = [
      { label: 'All Site Suppliers', value: 'all' },
      { label: 'Supplier Group', value: 'group' },
      { label: 'Individual Suppliers', value: 'individual' },
      { label: 'Supplier Locations', value: 'locations' },
    ]
    const scope = ref('all')
    const selectedGroupId = ref('')
    const selectedSupplierIds = ref([])

    // Location scope: one supplier + a subset of its locations
    const selectedLocationSupplierId = ref('')
    const selectedLocationIds = ref([])

    // Only multi-location suppliers are pickable here; single-location suppliers
    // are covered by the "Individual Suppliers" scope.
    const locationSupplierOptions = computed(() =>
      suppliers.value
        .filter(s => Array.isArray(s.locations) && s.locations.length > 0)
        .map(s => ({
          label: s.suppliername || s.name || `Supplier ${s.supplierid}`,
          value: String(s.supplierid),
        }))
    )

    const selectedSupplierLocations = computed(() => {
      const sup = suppliers.value.find(
        s => String(s.supplierid) === String(selectedLocationSupplierId.value)
      )
      return sup && Array.isArray(sup.locations) ? sup.locations : []
    })

    // Changing supplier clears the chosen locations (they belong to the old one).
    watch(selectedLocationSupplierId, () => { selectedLocationIds.value = [] })

    // Date range defaults: first and last day of current month
    const now = new Date()
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    const effectiveEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    const todayOrEnd = now < effectiveEnd ? now : effectiveEnd

    const formatDateISO = (d) => {
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    const startDate = ref(formatDateISO(firstDay))
    const endDate = ref(formatDateISO(todayOrEnd))
    const maxDate = formatDateISO(now)

    const formatOptions = [
      { label: 'PDF', value: 'pdf' },
      { label: 'CSV / Excel', value: 'csv' },
      { label: 'Both', value: 'both' },
    ]
    const format = ref('pdf')

    const recipientList = ref([''])
    // Index-aligned inline validation errors — invalid recipients used to be
    // silently dropped from the send with no warning.
    const recipientErrors = ref([''])

    const validateRecipient = (idx) => {
      const v = (recipientList.value[idx] || '').trim()
      recipientErrors.value[idx] = v && !v.includes('@') ? 'Enter a valid email address' : ''
    }

    const removeRecipient = (idx) => {
      recipientList.value.splice(idx, 1)
      recipientErrors.value.splice(idx, 1)
    }

    // Validates every row; returns true (and shows inline errors) if any
    // non-empty entry is not a usable email.
    const hasInvalidRecipients = () => {
      recipientList.value.forEach((_, idx) => validateRecipient(idx))
      return recipientErrors.value.some(Boolean)
    }

    const scheduleEnabled = ref(false)
    const frequencyOptions = [
      { label: 'Monthly', value: 'monthly' },
      { label: 'Weekly', value: 'weekly' },
      { label: 'Bi-weekly', value: 'biweekly' },
    ]
    const frequency = ref('monthly')
    const scheduleName = ref('')
    const scheduleNameError = ref('')
    const scheduleNameInput = ref(null)

    // Which builder action is in flight: 'download'|'email'|'emailDownload'|'schedule'.
    // Per-action so each button shows its own spinner (the old detached spinner
    // didn't say which of the three actions was running).
    const pendingAction = ref(null)
    const successMsg = ref('')

    // ---- Supplier List from Firestore ----
    const suppliers = ref([])

    const supplierOptions = computed(() =>
      suppliers.value.map(s => ({
        label: s.suppliername || s.name || `Supplier ${s.supplierid}`,
        value: String(s.supplierid),
      }))
    )

    const fetchSuppliersFromFirestore = () => {
      const colRef = query(collection(db, 'suppliers'), orderBy('suppliername'))
      const unsub = onSnapshot(colRef, (snapshot) => {
        const result = []
        snapshot.docs.forEach((doc) => {
          const data = doc.data()
          if (String(data.siteid) === String(store.state.currentSite)) {
            result.push(data)
          }
        })
        suppliers.value = result
      })
      watchEffect((onInvalidate) => {
        onInvalidate(() => unsub())
      })
    }

    // ---- Computed Helpers ----
    const parsedRecipients = computed(() => {
      return recipientList.value
        .map(e => e.trim())
        .filter(e => e.length > 0 && e.includes('@'))
    })

    const hasRecipients = computed(() => parsedRecipients.value.length > 0)

    const currentScopeId = computed(() => {
      if (scope.value === 'group') return selectedGroupId.value
      if (scope.value === 'individual') return selectedSupplierIds.value.join(',')
      if (scope.value === 'locations') {
        return JSON.stringify({
          supplierId: selectedLocationSupplierId.value,
          locationIds: selectedLocationIds.value,
        })
      }
      return store.state.currentSite
    })

    // Gate the builder actions: a locations report needs a supplier AND >=1 location.
    const canGenerate = computed(() => {
      if (scope.value === 'locations') {
        return !!selectedLocationSupplierId.value && selectedLocationIds.value.length > 0
      }
      return true
    })

    // ---- Tabs (persisted via ?tab= so refresh keeps the active tab) ----
    const activeTab = ref(route.query.tab === 'schedules' ? 'schedules' : 'history')

    const setTab = (tab) => {
      activeTab.value = tab
      router.replace({ query: { ...route.query, tab } })
    }

    // ---- Report History ----
    const reportHistory = ref([])
    const historyLoading = ref(false)

    const historyColumns = [
      { key: 'generatedAt', label: 'Date Generated', sortable: true },
      { key: 'scope', label: 'Scope', sortable: true },
      { key: 'period', label: 'Period', sortable: false },
      { key: 'format', label: 'Format', sortable: true },
      { key: 'emailedTo', label: 'Emailed To', sortable: false },
    ]

    const loadHistory = async () => {
      historyLoading.value = true
      try {
        const data = await fetchReportHistory(store.state.currentSite)
        reportHistory.value = Array.isArray(data) ? data : []
      } catch {
        reportHistory.value = []
      } finally {
        historyLoading.value = false
      }
    }

    // ---- Schedules ----
    const schedules = ref([])
    const schedulesLoading = ref(false)

    const scheduleColumns = [
      { key: 'name', label: 'Name', sortable: true },
      { key: 'frequency', label: 'Frequency', sortable: true },
      { key: 'lastRun', label: 'Last Run', sortable: true },
      { key: 'status', label: 'Status', sortable: true, align: 'center' },
      { key: 'nextRun', label: 'Next Run', sortable: true },
      { key: 'runCount', label: 'Runs', sortable: true, align: 'right' },
      { key: 'enabled', label: 'Enabled', sortable: false, align: 'center' },
    ]

    const loadSchedules = async () => {
      schedulesLoading.value = true
      try {
        const data = await fetchSchedulesApi(store.state.currentSite)
        schedules.value = (Array.isArray(data) ? data : []).map(s => ({
          ...s,
          lastRun: s.lastRunAt || null,
          status: s.lastRunStatus || null,
          nextRun: s.nextRunAt || null,
          runCount: s.runCount || 0,
        }))
      } catch {
        schedules.value = []
      } finally {
        schedulesLoading.value = false
      }
    }

    // ---- Actions ----
    const handleDownload = async () => {
      if (scheduleEnabled.value) {
        await handleCreateSchedule()
        return
      }
      pendingAction.value = 'download'
      successMsg.value = ''
      error.value = null
      try {
        const siteId = store.state.currentSite
        await downloadReport(siteId, startDate.value, endDate.value, scope.value, currentScopeId.value, format.value)
        // downloadReport never throws — it reports failure via the shared error
        // ref. Only claim success when no error was raised.
        if (!error.value) {
          successMsg.value = 'Report downloaded successfully.'
        }
      } finally {
        pendingAction.value = null
      }
    }

    const handleEmail = async () => {
      if (!hasRecipients.value || hasInvalidRecipients()) return
      pendingAction.value = 'email'
      successMsg.value = ''
      try {
        const payload = {
          site_id: store.state.currentSite,
          start_date: startDate.value,
          end_date: endDate.value,
          scope: scope.value,
          scope_id: currentScopeId.value,
          format: format.value,
          recipients: parsedRecipients.value,
          report_title: 'Analytics Report',
        }
        await emailReport(payload)
        successMsg.value = 'Report emailed successfully.'
        loadHistory()
      } catch {
        // error handled by composable
      } finally {
        pendingAction.value = null
      }
    }

    const handleEmailAndDownload = async () => {
      if (!hasRecipients.value || hasInvalidRecipients()) return
      pendingAction.value = 'emailDownload'
      successMsg.value = ''
      error.value = null
      try {
        const siteId = store.state.currentSite
        const payload = {
          site_id: siteId,
          start_date: startDate.value,
          end_date: endDate.value,
          scope: scope.value,
          scope_id: currentScopeId.value,
          format: format.value,
          recipients: parsedRecipients.value,
          report_title: 'Analytics Report',
        }
        await Promise.all([
          downloadReport(siteId, startDate.value, endDate.value, scope.value, currentScopeId.value, format.value),
          emailReport(payload),
        ])
        // The download half never throws — it reports failure via the shared
        // error ref. Don't claim full success when the download failed.
        if (!error.value) {
          successMsg.value = 'Report downloaded and emailed successfully.'
        }
        loadHistory()
      } catch {
        // error handled by composable
      } finally {
        pendingAction.value = null
      }
    }

    const handleCreateSchedule = async () => {
      if (!scheduleName.value.trim()) {
        scheduleNameError.value = 'Please enter a schedule name.'
        nextTick(() => scheduleNameInput.value?.focus())
        return
      }
      if (hasInvalidRecipients()) return
      scheduleNameError.value = ''
      pendingAction.value = 'schedule'
      successMsg.value = ''
      try {
        const payload = {
          name: scheduleName.value.trim(),
          siteId: store.state.currentSite,
          scope: scope.value,
          scopeId: currentScopeId.value,
          frequency: frequency.value,
          format: format.value,
          recipients: parsedRecipients.value,
          createdBy: store.state.user?.email || store.state.user?.uid || 'unknown',
        }
        await createSchedule(payload)
        successMsg.value = `Schedule "${scheduleName.value}" created successfully.`
        scheduleName.value = ''
        scheduleEnabled.value = false
        loadSchedules()
        setTab('schedules')
      } catch (e) {
        error.value = e.response?.data?.detail || e.message || 'Failed to create schedule.'
      } finally {
        pendingAction.value = null
      }
    }

    // Row identity (not id) — history rows have no guaranteed row key.
    const pendingResendRow = ref(null)

    const resendReport = async (row) => {
      if (!row.emailedTo || !row.emailedTo.length || pendingResendRow.value) return
      pendingResendRow.value = row
      successMsg.value = ''
      try {
        const payload = {
          site_id: store.state.currentSite,
          start_date: row.startDate,
          end_date: row.endDate,
          scope: row.scope,
          scope_id: row.scopeId,
          format: row.format,
          recipients: row.emailedTo,
          report_title: 'Analytics Report (Re-send)',
        }
        await emailReport(payload)
        successMsg.value = 'Report re-sent successfully.'
      } catch {
        // error handled by composable
      } finally {
        pendingResendRow.value = null
      }
    }

    // ---- Edit Schedule Modal ----
    const showEditModal = ref(false)
    const editSaving = ref(false)
    const editingScheduleId = ref(null)
    const editForm = ref({
      name: '',
      frequency: 'monthly',
      format: 'pdf',
      recipients: [''],
      enabled: true,
      scope: '',
      scopeId: '',
      supplierIds: [],
      lastRunAt: null,
      nextRunAt: null,
      lastRunStatus: null,
      lastRunError: null,
      runCount: 0,
    })

    const openEditSchedule = (row) => {
      editingScheduleId.value = row.id
      // Parse supplier IDs from scopeId
      let parsedSupplierIds = []
      if (row.scope === 'individual' && row.scopeId) {
        try {
          const parsed = JSON.parse(row.scopeId)
          parsedSupplierIds = Array.isArray(parsed) ? parsed : [row.scopeId]
        } catch {
          parsedSupplierIds = row.scopeId.includes(',')
            ? row.scopeId.split(',').map(s => s.trim()).filter(Boolean)
            : [row.scopeId]
        }
      }
      editForm.value = {
        name: row.name || '',
        frequency: row.frequency || 'monthly',
        format: row.format || 'pdf',
        recipients: row.recipients && row.recipients.length ? [...row.recipients] : [''],
        enabled: row.enabled !== false,
        scope: row.scope || '',
        scopeId: row.scopeId || '',
        supplierIds: parsedSupplierIds,
        lastRunAt: row.lastRunAt || row.lastRun || null,
        nextRunAt: row.nextRunAt || row.nextRun || null,
        lastRunStatus: row.lastRunStatus || row.status || null,
        lastRunError: row.lastRunError || null,
        runCount: row.runCount || 0,
      }
      showEditModal.value = true
    }

    // Read-only "Supplier — Loc1, Loc2" for a locations schedule in the edit modal.
    const editLocationLabel = computed(() => {
      if (editForm.value.scope !== 'locations') return ''
      try {
        const parsed = JSON.parse(editForm.value.scopeId || '{}')
        const sup = suppliers.value.find(s => String(s.supplierid) === String(parsed.supplierId))
        const supName = sup ? (sup.suppliername || sup.name) : parsed.supplierId
        const ids = (parsed.locationIds || []).map(String)
        const locNames = sup && Array.isArray(sup.locations)
          ? sup.locations.filter(l => ids.includes(String(l.locationid))).map(l => l.locationname)
          : ids
        return `${supName} — ${locNames.join(', ')}`
      } catch { return '' }
    })

    const addRecipientField = () => {
      editForm.value.recipients.push('')
    }

    const saveScheduleEdit = async () => {
      editSaving.value = true
      try {
        const cleanRecipients = editForm.value.recipients
          .map(e => e.trim())
          .filter(e => e.length > 0 && e.includes('@'))

        // Build scopeId from selected suppliers
        const newScopeId = editForm.value.supplierIds.length
          ? editForm.value.supplierIds.join(',')
          : editForm.value.scopeId

        const newScope = editForm.value.supplierIds.length
          ? 'individual'
          : editForm.value.scope

        await updateSchedule(editingScheduleId.value, {
          name: editForm.value.name,
          frequency: editForm.value.frequency,
          format: editForm.value.format,
          recipients: cleanRecipients,
          enabled: editForm.value.enabled,
          scope: newScope,
          scopeId: newScopeId,
        })

        // Update local data
        const idx = schedules.value.findIndex(s => s.id === editingScheduleId.value)
        if (idx !== -1) {
          schedules.value[idx].name = editForm.value.name
          schedules.value[idx].frequency = editForm.value.frequency
          schedules.value[idx].format = editForm.value.format
          schedules.value[idx].recipients = cleanRecipients
          schedules.value[idx].enabled = editForm.value.enabled
          schedules.value[idx].scope = newScope
          schedules.value[idx].scopeId = newScopeId
        }

        showEditModal.value = false
        successMsg.value = `Schedule "${editForm.value.name}" updated.`
      } catch (e) {
        error.value = e.response?.data?.detail || e.message || 'Failed to update schedule.'
      } finally {
        editSaving.value = false
      }
    }

    const pendingToggleId = ref(null)

    const toggleScheduleEnabled = async (row) => {
      if (pendingToggleId.value === row.id) return
      pendingToggleId.value = row.id
      // Optimistic flip — the switch responds immediately and reverts on error.
      row.enabled = !row.enabled
      try {
        await updateSchedule(row.id, { enabled: row.enabled })
      } catch (e) {
        row.enabled = !row.enabled
        error.value = e.response?.data?.detail || e.message || 'Failed to update schedule.'
      } finally {
        pendingToggleId.value = null
      }
    }

    const pendingRunId = ref(null)

    const handleRunNow = async (row) => {
      if (pendingRunId.value) return
      pendingRunId.value = row.id
      successMsg.value = ''
      try {
        await runScheduleNow(row.id)
        successMsg.value = `Schedule "${row.name}" triggered successfully.`
        loadHistory()
      } catch (e) {
        error.value = e.response?.data?.detail || e.message || 'Failed to run schedule.'
      } finally {
        pendingRunId.value = null
      }
    }

    const deleteState = ref({ show: false, row: null, loading: false })

    const confirmDeleteSchedule = (row) => {
      deleteState.value = { show: true, row, loading: false }
    }

    const doDeleteSchedule = async () => {
      const row = deleteState.value.row
      if (!row) return
      deleteState.value.loading = true
      try {
        await deleteScheduleApi(row.id)
        schedules.value = schedules.value.filter(s => s.id !== row.id)
        successMsg.value = `Schedule "${row.name}" deleted.`
        deleteState.value.show = false
      } catch (e) {
        error.value = e.response?.data?.detail || e.message || 'Failed to delete schedule.'
      } finally {
        deleteState.value.loading = false
      }
    }

    // ---- Formatting Helpers ----
    const scopeLabel = (val) => {
      if (val === 'all') return 'All Suppliers'
      if (val === 'group') return 'Supplier Group'
      if (val === 'individual') return 'Individual'
      if (val === 'locations') return 'Supplier Locations'
      return val || '--'
    }

    // ---- Lifecycle ----
    onMounted(() => {
      store.commit('setCurrentRoute', 'Analytics Reports')
      fetchSuppliersFromFirestore()
      fetchGroups(store.state.currentSite)
      loadHistory()
      loadSchedules()
    })

    return {
      // State
      error,
      successMsg,
      scope,
      scopeOptions,
      selectedGroupId,
      selectedSupplierIds,
      supplierGroups,
      supplierOptions,
      startDate,
      endDate,
      maxDate,
      format,
      formatOptions,
      recipientList,
      recipientErrors,
      validateRecipient,
      removeRecipient,
      scheduleEnabled,
      frequency,
      frequencyOptions,
      scheduleName,
      scheduleNameError,
      scheduleNameInput,
      pendingAction,
      activeTab,
      setTab,
      // History
      reportHistory,
      historyLoading,
      historyColumns,
      // Schedules
      schedules,
      schedulesLoading,
      scheduleColumns,
      // Computed
      hasRecipients,
      // Methods
      handleDownload,
      handleEmail,
      handleEmailAndDownload,
      resendReport,
      pendingResendRow,
      toggleScheduleEnabled,
      pendingToggleId,
      handleRunNow,
      pendingRunId,
      confirmDeleteSchedule,
      deleteState,
      doDeleteSchedule,
      showEditModal,
      editSaving,
      editForm,
      openEditSchedule,
      addRecipientField,
      saveScheduleEdit,
      formatDateTime,
      scopeLabel,
      selectedLocationSupplierId,
      selectedLocationIds,
      locationSupplierOptions,
      selectedSupplierLocations,
      canGenerate,
      editLocationLabel,
    }
  },
}
</script>
